import { initReveal } from '../common/reveal';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const reducedMotionQuery = '(prefers-reduced-motion: reduce)';
const cinematicQuery = '(min-width: 900px) and (prefers-reduced-motion: no-preference)';

type StoryConfig = {
    id: string;
    section: HTMLElement;
    pin: HTMLElement;
    steps: HTMLElement[];
    progressTarget?: HTMLElement;
    progressProperty: string;
    toneAttribute?: string;
    toneDatasetKey?: string;
    containerStateTarget?: HTMLElement;
    onActivate?: (index: number, step: HTMLElement, animate: boolean, direction: number) => void;
    distanceFactor?: number;
    snap?: boolean;
    startOffset?: () => number;
};

type ScopeController = {
    section: HTMLElement;
    pin: HTMLElement;
    scope: HTMLElement;
    tabs: HTMLButtonElement[];
    activate: (index: number, animate?: boolean) => void;
};

const scopeControllers = new WeakMap<HTMLElement, ScopeController>();

const prefersReducedMotion = (): boolean =>
    window.matchMedia(reducedMotionQuery).matches;

const canUseCinematicScroll = (): boolean =>
    window.matchMedia(cinematicQuery).matches;

const getMentorNavOffset = (): number => {
    const header = document.querySelector<HTMLElement>('[data-site-header], .site-header, .main-header, .navbar, body > header');
    const measuredHeight = header?.getBoundingClientRect().height ?? 0;
    const fallbackHeight = window.innerWidth >= 1200 ? 88 : 76;
    return Math.round(Math.max(68, Math.min(128, measuredHeight || fallbackHeight)) + 8);
};

const clampIndex = (index: number, length: number): number =>
    Math.max(0, Math.min(index, Math.max(0, length - 1)));

const indexFromProgress = (progress: number, length: number): number =>
    clampIndex(Math.floor(Math.min(0.999, Math.max(0, progress)) * length), length);

const isPresent = <T>(item: T | null | undefined): item is T =>
    item !== null && item !== undefined;

const childrenOf = (item: HTMLElement | null | undefined): HTMLElement[] =>
    Array.from(item?.children ?? []).filter((child): child is HTMLElement => child instanceof HTMLElement);

const setPercent = (target: HTMLElement | undefined, property: string, value: number): void => {
    target?.style.setProperty(property, `${Math.max(0.04, Math.min(1, value)) * 100}%`);
};

const animateIn = (items: Array<HTMLElement | null | undefined>, y = 16, direction = 1): void => {
    const targets = items.filter((item): item is HTMLElement => Boolean(item));
    if (!targets.length || prefersReducedMotion()) {
        return;
    }

    gsap.killTweensOf(targets);
    gsap.fromTo(
        targets,
        { autoAlpha: 0, y: direction * y, filter: 'blur(8px)' },
        {
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            duration: 0.44,
            stagger: 0.04,
            ease: 'power3.out',
            overwrite: 'auto',
            clearProps: 'filter,opacity,visibility'
        }
    );
};

const setExclusiveVisual = (
    visuals: HTMLElement[],
    activeKey: string,
    datasetKey: string,
    animate: boolean,
    direction: number,
    rotation = { rotateY: -4, rotateX: 2.5 }
): HTMLElement | null => {
    if (!visuals.length) {
        return null;
    }

    let activeVisual: HTMLElement | null = null;
    gsap.killTweensOf(visuals);

    visuals.forEach(visual => {
        const isActive = visual.dataset[datasetKey] === activeKey;
        visual.classList.toggle('is-active', isActive);
        visual.setAttribute('aria-hidden', String(!isActive));

        if (isActive) {
            activeVisual = visual;
            return;
        }

        gsap.set(visual, {
            autoAlpha: 0,
            y: direction * -10,
            rotateY: rotation.rotateY,
            rotateX: rotation.rotateX,
            scale: 0.985,
            zIndex: 0,
            overwrite: true
        });
    });

    if (!activeVisual) {
        return null;
    }

    if (!animate || prefersReducedMotion()) {
        gsap.set(activeVisual, {
            autoAlpha: 1,
            y: 0,
            rotateY: rotation.rotateY,
            rotateX: rotation.rotateX,
            scale: 1,
            zIndex: 1,
            clearProps: 'opacity,visibility,transform,zIndex'
        });
        return activeVisual;
    }

    gsap.fromTo(
        activeVisual,
        {
            autoAlpha: 0,
            y: direction * 18,
            rotateY: direction > 0 ? rotation.rotateY - 4 : rotation.rotateY + 3,
            rotateX: rotation.rotateX,
            scale: 0.985,
            zIndex: 1
        },
        {
            autoAlpha: 1,
            y: 0,
            rotateY: rotation.rotateY,
            rotateX: rotation.rotateX,
            scale: 1,
            zIndex: 1,
            duration: 0.48,
            ease: 'power3.out',
            overwrite: 'auto',
            clearProps: 'opacity,visibility,transform,zIndex'
        }
    );

    return activeVisual;
};

const storyDistance = (steps: HTMLElement[], factor = 0.72): number => {
    const viewport = window.innerHeight || 900;
    return Math.max(viewport * 1.25, viewport * Math.max(steps.length, 1) * factor);
};

const heroTransitionDistance = (): number =>
    Math.max(window.innerHeight * 0.92, 720);

function createPinnedStory(config: StoryConfig): ScrollTrigger | null {
    const {
        id,
        section,
        pin,
        steps,
        progressTarget = section,
        progressProperty,
        toneAttribute,
        toneDatasetKey,
        containerStateTarget,
        onActivate,
        distanceFactor,
        snap,
        startOffset = getMentorNavOffset
    } = config;

    if (!steps.length) {
        return null;
    }

    let activeIndex = -1;

    const activate = (index: number, animate = true): void => {
        const safeIndex = clampIndex(index, steps.length);
        if (safeIndex === activeIndex) {
            return;
        }

        const direction = activeIndex === -1 || safeIndex >= activeIndex ? 1 : -1;
        activeIndex = safeIndex;
        const activeStep = steps[safeIndex];

        if (toneAttribute && toneDatasetKey && activeStep) {
            const tone = activeStep.dataset[toneDatasetKey] ?? '';
            if (tone) {
                (containerStateTarget ?? section).dataset[toneAttribute] = tone;
            }
        }

        section.dataset.activeIndex = String(safeIndex);
        steps.forEach((step, stepIndex) => {
            const isActive = stepIndex === safeIndex;
            step.classList.toggle('is-active', isActive);
            if (step.hasAttribute('data-kaizen-step')) {
                step.setAttribute('aria-pressed', String(isActive));
            }
        });

        setPercent(progressTarget, progressProperty, (safeIndex + 1) / steps.length);
        onActivate?.(safeIndex, activeStep, animate, direction);
    };

    activate(0, false);

    if (!canUseCinematicScroll()) {
        setPercent(progressTarget, progressProperty, 1);
        return null;
    }

    const triggerOptions: ScrollTrigger.Vars = {
        id,
        trigger: section,
        start: () => `top ${startOffset()}px`,
        end: () => `+=${storyDistance(steps, distanceFactor)}`,
        pin,
        scrub: 0.65,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: self => {
            setPercent(progressTarget, progressProperty, self.progress);
            activate(indexFromProgress(self.progress, steps.length));
        }
    };

    if (snap && steps.length > 1) {
        Object.assign(triggerOptions, {
            snap: {
                snapTo: 1 / (steps.length - 1),
                duration: { min: 0.14, max: 0.32 },
                delay: 0.015,
                ease: 'power2.out'
            }
        });
    }

    const trigger = ScrollTrigger.create(triggerOptions);

    return trigger;
}

function initMentorHeroIntro(root: HTMLElement): void {
    const hero = root.querySelector<HTMLElement>('[data-mentor-hero]');
    if (!hero) {
        return;
    }

    const tiles = Array.from(hero.querySelectorAll<HTMLElement>('[data-mentor-hero-tile]'));
    const revealItems = Array.from(hero.querySelectorAll<HTMLElement>('[data-mentor-hero-reveal]'));
    const atmosphere = hero.querySelector<HTMLElement>('.mentorpage-hero__atmosphere');
    const glows = Array.from(hero.querySelectorAll<HTMLElement>('.mentorpage-hero__glow'));
    const grid = hero.querySelector<HTMLElement>('.mentorpage-hero__grid');

    const settle = (): void => {
        hero.style.setProperty('--hero-visual-opacity', '1');
        gsap.set(hero, { autoAlpha: 1, clearProps: 'visibility,opacity' });
        gsap.set(tiles, { autoAlpha: 1, y: 0, scale: 1, rotate: 0, clearProps: 'visibility,opacity,transform' });
        gsap.set(revealItems, { autoAlpha: 1, y: 0, clearProps: 'visibility,opacity,transform' });
        gsap.set([atmosphere, grid, ...glows].filter(isPresent), { autoAlpha: 1, scale: 1, clearProps: 'visibility,opacity,transform' });
        hero.classList.add('is-hero-settled');
    };

    if (prefersReducedMotion() || !canUseCinematicScroll()) {
        root.classList.add('is-reduced-motion');
        settle();
        return;
    }

    gsap.set(tiles, {
        autoAlpha: 0,
        y: 42,
        scale: 0.9,
        rotate: index => (index % 2 === 0 ? -3 : 3)
    });
    gsap.set(revealItems, { autoAlpha: 0, y: 30 });
    gsap.set(atmosphere, { autoAlpha: 0.35 });
    gsap.set(grid, { autoAlpha: 0, y: 20 });
    gsap.set(glows, { autoAlpha: 0, scale: 0.72 });

    let settled = false;
    let timeline: gsap.core.Timeline | null = null;
    const settleOnce = (): void => {
        if (settled) {
            return;
        }

        settled = true;
        timeline?.progress(1).kill();
        settle();
    };

    timeline = gsap.timeline({
        defaults: { ease: 'power3.out' },
        onComplete: settleOnce
    });

    timeline
        .to(atmosphere, {
            autoAlpha: 1,
            duration: 0.54
        }, 0)
        .to(glows, {
            autoAlpha: 1,
            scale: 1,
            duration: 1.15,
            stagger: 0.08
        }, 0.02)
        .to(grid, {
            autoAlpha: 1,
            y: 0,
            duration: 0.92
        }, 0.08)
        .to(tiles, {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            rotate: 0,
            duration: 1,
            stagger: 0.085
        }, 0.04)
        .to(revealItems, {
            autoAlpha: 1,
            y: 0,
            duration: 0.72,
            stagger: 0.09
        }, 0.34);

    window.setTimeout(settleOnce, 2600);
}

function initMentorHeroStory(root: HTMLElement): void {
    const hero = root.querySelector<HTMLElement>('[data-mentor-hero]');
    if (!hero || !canUseCinematicScroll()) {
        return;
    }

    const tiles = Array.from(hero.querySelectorAll<HTMLElement>('[data-mentor-hero-tile]'));
    const revealItems = Array.from(hero.querySelectorAll<HTMLElement>('[data-mentor-hero-reveal]'));
    const atmosphere = hero.querySelector<HTMLElement>('.mentorpage-hero__atmosphere');
    const glows = Array.from(hero.querySelectorAll<HTMLElement>('.mentorpage-hero__glow'));
    const grid = hero.querySelector<HTMLElement>('.mentorpage-hero__grid');

    gsap.timeline({
        scrollTrigger: {
            id: 'mentor-hero-story',
            trigger: hero,
            start: 'top top',
            end: () => `+=${heroTransitionDistance()}`,
            pin: hero,
            pinSpacing: false,
            scrub: 0.7,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onUpdate: self => hero.style.setProperty('--hero-story-progress', String(self.progress.toFixed(3)))
        }
    })
        .set(hero, { autoAlpha: 1 }, 0)
        .set(tiles, { autoAlpha: 1 }, 0)
        .to(hero, { '--hero-visual-opacity': 1, duration: 0.44, ease: 'none' }, 0)
        .to(tiles, {
            y: index => (index % 2 === 0 ? -28 : 24),
            scale: index => (index % 3 === 0 ? 1.04 : 0.97),
            autoAlpha: 1,
            duration: 0.58,
            ease: 'none'
        }, 0)
        .to(tiles, {
            y: index => (index % 2 === 0 ? -132 : 104),
            scale: 0.86,
            autoAlpha: 0,
            duration: 0.42,
            ease: 'none'
        }, 0.58)
        .to([atmosphere, grid, ...glows].filter(isPresent), {
            autoAlpha: 0,
            scale: 1.08,
            duration: 0.42,
            ease: 'none'
        }, 0.6)
        .to(revealItems, {
            y: -52,
            autoAlpha: 0,
            filter: 'blur(8px)',
            stagger: 0.015,
            duration: 0.46,
            ease: 'power1.inOut'
        }, 0.52)
        .to(hero, {
            autoAlpha: 0,
            duration: 0.3,
            ease: 'none'
        }, 0.7);
}

function initIntakeJourney(root: HTMLElement): void {
    const section = root.querySelector<HTMLElement>('[data-intake-journey]');
    const pin = section?.querySelector<HTMLElement>('.mentorpage-intake__sticky');
    const steps = Array.from(section?.querySelectorAll<HTMLElement>('[data-intake-step]') ?? []);
    const visuals = Array.from(section?.querySelectorAll<HTMLElement>('[data-intake-visual]') ?? []);

    if (!section || !pin || !steps.length) {
        return;
    }

    createPinnedStory({
        id: 'mentor-intake-story',
        section,
        pin,
        steps,
        progressProperty: '--intake-progress',
        toneAttribute: 'activeTone',
        toneDatasetKey: 'intakeTone',
        distanceFactor: 0.86,
        snap: true,
        onActivate: (_index, step, animate, direction) => {
            const tone = step.dataset.intakeTone ?? '';
            setExclusiveVisual(visuals, tone, 'intakeVisual', animate, direction, { rotateY: -4, rotateX: 2.5 });
        }
    });
}

function initStandardScroll(root: HTMLElement): void {
    const section = root.querySelector<HTMLElement>('[data-standard-scroll-section]');
    const pin = section?.querySelector<HTMLElement>('[data-standard-stage]');
    const steps = Array.from(section?.querySelectorAll<HTMLElement>('[data-standard-step]') ?? []);
    const seal = section?.querySelector<HTMLElement>('.mentorpage-standard__seal');

    if (!section || !pin || !steps.length) {
        return;
    }

    createPinnedStory({
        id: 'mentor-standard-story',
        section,
        pin,
        steps,
        progressProperty: '--standard-progress',
        distanceFactor: 0.82,
        snap: false,
        onActivate: (_index, step, animate, direction) => {
            if (!animate) {
                return;
            }

            if (seal && !prefersReducedMotion()) {
                gsap.fromTo(
                    seal,
                    { scale: 0.985, rotateZ: direction > 0 ? 0.8 : -0.4 },
                    { scale: 1, rotateZ: 0, duration: 0.52, ease: 'power3.out', overwrite: 'auto', clearProps: 'transform' }
                );
            }
        }
    });
}

function getMentorScopeController(root: HTMLElement): ScopeController | null {
    const section = root.querySelector<HTMLElement>('[data-mentor-scope-section]');
    const pin = section?.querySelector<HTMLElement>('.mentorpage-scope__sticky');
    const scope = section?.querySelector<HTMLElement>('[data-mentor-scope]');
    const tabs = Array.from(scope?.querySelectorAll<HTMLButtonElement>('[data-mentor-scope-tab]') ?? []);
    const visuals = Array.from(scope?.querySelectorAll<HTMLElement>('[data-mentor-scope-visual-panel]') ?? []);

    if (!section || !pin || !scope || !tabs.length) {
        return null;
    }

    const existing = scopeControllers.get(scope);
    if (existing) {
        return existing;
    }

    let activeIndex = -1;

    const activate = (index: number, animate = true): void => {
        const safeIndex = clampIndex(index, tabs.length);
        if (safeIndex === activeIndex) {
            return;
        }

        const direction = activeIndex === -1 || safeIndex >= activeIndex ? 1 : -1;
        activeIndex = safeIndex;
        const activeTab = tabs[safeIndex];
        const key = activeTab?.dataset.mentorScopeTab ?? '';

        if (!key) {
            return;
        }

        section.dataset.activeScope = key;
        scope.dataset.activeScope = key;
        setPercent(scope, '--scope-progress', (safeIndex + 1) / tabs.length);

        tabs.forEach((tab, tabIndex) => {
            const isActive = tabIndex === safeIndex;
            tab.classList.toggle('is-active', isActive);
            tab.setAttribute('aria-pressed', String(isActive));
            tab.tabIndex = isActive ? 0 : -1;
        });

        setExclusiveVisual(visuals, key, 'mentorScopeVisualPanel', animate, direction, { rotateY: -4, rotateX: 2.5 });
    };

    tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(index));
        tab.addEventListener('keydown', event => {
            if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
                return;
            }

            event.preventDefault();

            let nextIndex = index;
            if (event.key === 'Home') {
                nextIndex = 0;
            } else if (event.key === 'End') {
                nextIndex = tabs.length - 1;
            } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                nextIndex = (index - 1 + tabs.length) % tabs.length;
            } else {
                nextIndex = (index + 1) % tabs.length;
            }

            tabs[nextIndex]?.focus();
            activate(nextIndex);
        });
    });

    activate(0, false);

    const controller = { section, pin, scope, tabs, activate };
    scopeControllers.set(scope, controller);
    return controller;
}

function initMentorScope(root: HTMLElement, enableStory = false): void {
    const controller = getMentorScopeController(root);

    if (!controller || !enableStory) {
        return;
    }

    createPinnedStory({
        id: 'mentor-scope-story',
        section: controller.section,
        pin: controller.pin,
        steps: controller.tabs,
        progressTarget: controller.scope,
        progressProperty: '--scope-progress',
        distanceFactor: 0.78,
        snap: true,
        onActivate: index => controller.activate(index)
    });
}

function initKaizenEngine(root: HTMLElement): void {
    const section = root.querySelector<HTMLElement>('[data-kaizen-scroll-section]');
    const pin = section?.querySelector<HTMLElement>('.mentorpage-kaizen__sticky');
    const engine = section?.querySelector<HTMLElement>('[data-kaizen-engine]');
    const steps = Array.from(engine?.querySelectorAll<HTMLButtonElement>('[data-kaizen-step]') ?? []);

    if (!section || !pin || !engine || !steps.length) {
        return;
    }

    const setKaizenActive = (index: number): void => {
        const safeIndex = clampIndex(index, steps.length);
        const activeStep = steps[safeIndex];

        engine.dataset.activeIndex = String(safeIndex);
        section.dataset.activeTone = activeStep?.dataset.kaizenTone ?? 'observe';
        setPercent(section, '--kaizen-progress', (safeIndex + 1) / steps.length);

        steps.forEach((item, itemIndex) => {
            const isActive = itemIndex === safeIndex;
            item.classList.toggle('is-active', isActive);
            item.setAttribute('aria-pressed', String(isActive));
        });
    };

    steps.forEach((step, index) => {
        step.addEventListener('click', () => {
            setKaizenActive(index);
        });
    });

    setKaizenActive(0);

    createPinnedStory({
        id: 'mentor-kaizen-story',
        section,
        pin,
        steps,
        progressProperty: '--kaizen-progress',
        toneAttribute: 'activeTone',
        toneDatasetKey: 'kaizenTone',
        containerStateTarget: engine,
        distanceFactor: 0.82,
        snap: true,
        onActivate: (index, step, animate, direction) => {
            setKaizenActive(index);

            if (animate) {
                animateIn(childrenOf(step), 14, direction);
            }
        }
    });
}

function initFormatFlow(root: HTMLElement): void {
    const section = root.querySelector<HTMLElement>('[data-format-scroll-section]');
    const pin = section?.querySelector<HTMLElement>('.mentorpage-format__sticky');
    const flow = section?.querySelector<HTMLElement>('[data-format-flow]');
    const steps = Array.from(flow?.querySelectorAll<HTMLElement>('[data-format-step]') ?? []);

    if (!section || !pin || !flow || !steps.length) {
        return;
    }

    createPinnedStory({
        id: 'mentor-format-story',
        section,
        pin,
        steps,
        progressTarget: flow,
        progressProperty: '--format-progress',
        distanceFactor: 0.76,
        snap: true,
        onActivate: (_index, step, animate, direction) => {
            flow.dataset.activeIndex = String(_index);
            if (animate) {
                animateIn(childrenOf(step), 14, direction);
            }
        }
    });
}

function initMentorFaq(root: HTMLElement): void {
    const list = root.querySelector<HTMLElement>('[data-mentor-faq]');

    if (!list) {
        return;
    }

    const items = Array.from(list.querySelectorAll<HTMLElement>('.mentor-faq-item'));

    const syncHeight = (item: HTMLElement): void => {
        const answer = item.querySelector<HTMLElement>('.mentor-faq-item__answer');
        if (!answer) {
            return;
        }

        answer.hidden = false;
        answer.style.setProperty('--faq-height', `${answer.scrollHeight}px`);
        answer.setAttribute('aria-hidden', String(!item.classList.contains('is-open')));
    };

    const setOpen = (item: HTMLElement, open: boolean): void => {
        const button = item.querySelector<HTMLButtonElement>('button[aria-expanded]');
        const answer = item.querySelector<HTMLElement>('.mentor-faq-item__answer');

        if (!button || !answer) {
            return;
        }

        answer.hidden = false;
        answer.style.setProperty('--faq-height', `${answer.scrollHeight}px`);
        item.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', String(open));
        answer.setAttribute('aria-hidden', String(!open));
    };

    items.forEach(item => {
        const button = item.querySelector<HTMLButtonElement>('button[aria-expanded]');
        const answer = item.querySelector<HTMLElement>('.mentor-faq-item__answer');

        if (!button || !answer) {
            return;
        }

        answer.hidden = false;
        syncHeight(item);

        button.addEventListener('click', () => {
            setOpen(item, !item.classList.contains('is-open'));
        });
    });

    if (!prefersReducedMotion() && items.length) {
        gsap.fromTo(
            items,
            { autoAlpha: 0, y: 22 },
            {
                autoAlpha: 1,
                y: 0,
                duration: 0.5,
                stagger: 0.055,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: root.querySelector<HTMLElement>('[data-mentor-faq-section]') ?? list,
                    start: 'top 72%',
                    once: true
                }
            }
        );
    }

    window.addEventListener('resize', () => {
        items.forEach(syncHeight);
    }, { passive: true });
}

function getMentorHashTargetTop(target: HTMLElement): number {
    const section = target.closest('.mentorpage-section');
    const scrollTarget = section instanceof HTMLElement ? section : target;
    const naturalTop = window.scrollY + scrollTarget.getBoundingClientRect().top - getMentorNavOffset();

    if (scrollTarget.hasAttribute('data-mentor-hero')) {
        return 0;
    }

    return Math.max(0, naturalTop);
}

function alignMentorHash(root: HTMLElement, behavior: ScrollBehavior = 'auto'): void {
    const hash = window.location.hash;
    if (!hash.startsWith('#mentor-')) {
        return;
    }

    const targetId = window.decodeURIComponent(hash.slice(1));
    const target = document.getElementById(targetId);
    if (!target || !root.contains(target)) {
        return;
    }

    ScrollTrigger.refresh();
    window.scrollTo({
        top: getMentorHashTargetTop(target),
        behavior: prefersReducedMotion() ? 'auto' : behavior
    });
    window.setTimeout(() => ScrollTrigger.update(), 80);
}

function initMentorHashNavigation(root: HTMLElement): void {
    const links = Array.from(root.querySelectorAll<HTMLAnchorElement>('a[href^="#mentor-"]'));

    links.forEach(link => {
        link.addEventListener('click', event => {
            const hash = link.hash;
            if (!hash) {
                return;
            }

            const target = document.getElementById(window.decodeURIComponent(hash.slice(1)));
            if (!target || !root.contains(target)) {
                return;
            }

            event.preventDefault();
            window.history.pushState(null, '', hash);
            alignMentorHash(root, 'smooth');
        });
    });

    window.addEventListener('hashchange', () => {
        window.setTimeout(() => alignMentorHash(root, 'smooth'), 40);
    });
}

function initCinematicStories(root: HTMLElement): void {
    if (prefersReducedMotion()) {
        root.classList.add('is-reduced-motion');
        return;
    }

    const media = gsap.matchMedia();

    media.add(cinematicQuery, () => {
        root.classList.add('is-cinematic-scroll');

        const context = gsap.context(() => {
            initMentorHeroStory(root);
            initIntakeJourney(root);
            initStandardScroll(root);
            initMentorScope(root, true);
            initKaizenEngine(root);
            initFormatFlow(root);
            ScrollTrigger.refresh();
        }, root);

        return () => {
            context.revert();
            root.classList.remove('is-cinematic-scroll');
        };
    });

    media.add('(max-width: 899px), (prefers-reduced-motion: reduce)', () => {
        root.classList.toggle('is-reduced-motion', prefersReducedMotion());
        setPercent(root.querySelector<HTMLElement>('[data-intake-journey]') ?? undefined, '--intake-progress', 1);
        setPercent(root.querySelector<HTMLElement>('[data-standard-scroll-section]') ?? undefined, '--standard-progress', 1);
        setPercent(root.querySelector<HTMLElement>('[data-mentor-scope]') ?? undefined, '--scope-progress', 1);
        setPercent(root.querySelector<HTMLElement>('[data-kaizen-scroll-section]') ?? undefined, '--kaizen-progress', 1);
        setPercent(root.querySelector<HTMLElement>('[data-format-flow]') ?? undefined, '--format-progress', 1);
    });
}

const mentorRoot = document.getElementById('mentorPage');

if (mentorRoot) {
    initReveal({ selector: '#mentorPage [data-reveal]', threshold: 0.18 });
    initMentorHeroIntro(mentorRoot);
    initMentorScope(mentorRoot);
    initMentorFaq(mentorRoot);
    initCinematicStories(mentorRoot);
    initMentorHashNavigation(mentorRoot);

    window.addEventListener('load', () => {
        ScrollTrigger.refresh();
        window.setTimeout(() => alignMentorHash(mentorRoot), 160);
    }, { once: true });
    window.addEventListener('resize', () => ScrollTrigger.refresh(), { passive: true });
}
